import { useEffect, useState } from "react";
import { Card, CardContent } from "../../../components/ui/card";
import { TrendingUp, ExternalLink, Sparkles } from "lucide-react";
import api from "../../../lib/api";
import PriceHikeSuggestionDialog from "./PriceHikeSuggestionDialog";

const fmtEUR = (n) => new Intl.NumberFormat("es-ES", {
  style: "currency", currency: "EUR",
}).format(n || 0);
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("es-ES", { day: "numeric", month: "short" }); }
  catch { return iso; }
};

/**
 * Alerts the owner about ingredient price increases detected during the last
 * invoice syncs (>10% vs previous cost). Self-hides when empty.
 */
export function PriceHikesCard({ restaurantId, onOpenDocument }) {
  const [data, setData] = useState(null);
  const [aiTarget, setAiTarget] = useState(null); // { ingredientId, name }

  useEffect(() => {
    if (!restaurantId) return;
    let cancelled = false;
    api.listRecentPriceHikes(restaurantId, 30, 10)
      .then((r) => { if (!cancelled) setData(r); })
      .catch(() => { if (!cancelled) setData({ hikes: [] }); });
    return () => { cancelled = true; };
  }, [restaurantId]);

  if (!data || !data.hikes || data.hikes.length === 0) return null;

  return (
    <Card
      data-testid="price-hikes-card"
      className="border-amber-200 bg-amber-50/40"
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-2 mb-3">
          <TrendingUp className="w-4 h-4 text-amber-700 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-semibold text-sm text-amber-900">
              Subidas de precio detectadas
            </h3>
            <p className="text-[11px] text-amber-800">
              Últimos 30 días · {data.count} ingrediente{data.count === 1 ? "" : "s"} con coste mayor que antes
            </p>
          </div>
        </div>

        <ul className="divide-y divide-amber-100 max-h-72 overflow-y-auto pr-1">
          {data.hikes.map((h) => {
            const strong = h.pct_change >= 20;
            return (
              <li
                key={h.ingredient_id}
                className={`flex items-center gap-3 py-2 text-xs px-2 -mx-2 rounded ${strong ? "ring-1 ring-amber-200/50" : ""}`}
                data-testid={`price-hike-${h.ingredient_id}`}
              >
                <div className="shrink-0 h-8 w-8 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center">
                  <TrendingUp className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate text-amber-950">{h.name}</div>
                  <div className="text-[11px] text-amber-800 truncate">
                    {fmtEUR(h.previous_price)} → <strong>{fmtEUR(h.new_price)}</strong>
                    {h.unit && <span className="opacity-70"> /{h.unit}</span>}
                    <span className="mx-1">·</span>
                    <span className="opacity-80">{h.supplier_name || "sin proveedor"}</span>
                  </div>
                </div>
                <div className="shrink-0 text-right">
                  <div className={`font-bold tabular-nums ${strong ? "text-red-700" : "text-amber-700"}`}>
                    +{h.pct_change.toFixed(1)}%
                  </div>
                  <div className="text-[10px] text-muted-foreground">{fmtDate(h.detected_at)}</div>
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setAiTarget({ ingredientId: h.ingredient_id, name: h.name });
                  }}
                  className="shrink-0 rounded-full p-1.5 bg-purple-100 text-purple-700 hover:bg-purple-200 transition"
                  title="Ver sugerencia IA de cotización alternativa"
                  data-testid={`ai-suggestion-btn-${h.ingredient_id}`}
                >
                  <Sparkles className="w-3.5 h-3.5" />
                </button>
                <ExternalLink
                  className="w-3 h-3 text-amber-700 shrink-0 cursor-pointer"
                  onClick={(e) => { e.stopPropagation(); onOpenDocument?.(h.document_id); }}
                />
              </li>
            );
          })}
        </ul>
      </CardContent>

      {aiTarget && (
        <PriceHikeSuggestionDialog
          open={!!aiTarget}
          onClose={() => setAiTarget(null)}
          restaurantId={restaurantId}
          ingredientId={aiTarget.ingredientId}
          ingredientName={aiTarget.name}
        />
      )}
    </Card>
  );
}

export default PriceHikesCard;
